import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid3x3, X } from 'lucide-react';
import type { Term } from '@/engine';

interface TableProps {
  bMax: number;
  terms: readonly Term[];
  a: readonly number[];
}

/** Vertical B × {A values} table for the hover popover: one column up to 26 rows, otherwise two; the question's products are marked. */
function Table({ bMax, terms, a: as }: TableProps) {
  const hit = new Set(terms.map((t) => `${t.b}:${t.a}`));
  const rowHit = new Set(terms.map((t) => t.b));
  const cols = bMax > 26 ? 2 : 1;
  const chunk = Math.ceil(bMax / cols);
  const tables = [];
  for (let s0 = 1; s0 <= bMax; s0 += chunk) {
    const rows = [];
    for (let b = s0; b < s0 + chunk && b <= bMax; b++) {
      rows.push(
        <tr key={b} data-hit={rowHit.has(b) || undefined}>
          <td>{b}</td>
          {as.map((a) => (
            <td key={a} data-hit={hit.has(`${b}:${a}`) || undefined}>
              {a * b}
            </td>
          ))}
        </tr>,
      );
    }
    tables.push(
      <table key={s0}>
        <thead>
          <tr>
            <th>B</th>
            {as.map((a) => (
              <th key={a}>×{a}</th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>,
    );
  }
  return <div className="mt">{tables}</div>;
}

/** Horizontal table for the pinned panel: B runs along the top in strips of up to 26, one row per A value. */
function WideTable({ bMax, terms, a: as }: TableProps) {
  const hit = new Set(terms.map((t) => `${t.b}:${t.a}`));
  const colHit = new Set(terms.map((t) => t.b));
  const strip = 26;
  const tables = [];
  for (let s0 = 1; s0 <= bMax; s0 += strip) {
    const bs: number[] = [];
    for (let b = s0; b < s0 + strip && b <= bMax; b++) bs.push(b);
    tables.push(
      <table key={s0}>
        <thead>
          <tr>
            <th>B</th>
            {bs.map((b) => (
              <th key={b} data-hit={colHit.has(b) || undefined}>
                {b}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {as.map((a) => (
            <tr key={a}>
              <td>×{a}</td>
              {bs.map((b) => (
                <td
                  key={b}
                  data-hit={hit.has(`${b}:${a}`) || undefined}
                  data-col={colHit.has(b) || undefined}
                >
                  {a * b}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>,
    );
  }
  return <div className="mt mt-wide">{tables}</div>;
}

/** HUD button: hover shows the vertical table under it; click toggles the pinned panel (rendered by `HintPanel`). */
export function HintButton({
  bMax,
  terms,
  a,
  pinned,
  onToggle,
}: TableProps & { pinned: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);

  return (
    <div className="relative self-center" onMouseLeave={() => setHover(false)}>
      <button
        type="button"
        title={t('play.table')}
        aria-expanded={hover || pinned}
        onMouseEnter={() => setHover(true)}
        onClick={onToggle}
        data-on={hover || pinned || undefined}
        className="grid size-11 place-items-center rounded-xl border border-line-strong bg-surface text-fg-muted transition-colors hover:border-accent hover:text-accent data-on:border-accent data-on:text-accent"
      >
        <Grid3x3 className="size-[22px]" strokeWidth={1.8} />
      </button>

      {hover && !pinned && (
        <div
          className="hint-box absolute top-[calc(100%+10px)] right-0 z-30 max-md:right-auto max-md:left-1/2 max-md:-translate-x-1/2"
          role="dialog"
        >
          <div className="label mb-2">{`${t('home.difficulty')} ${bMax}`}</div>
          <Table bMax={bMax} terms={terms} a={a} />
        </div>
      )}
    </div>
  );
}

/** Pinned panel in the page flow under the answers: the wide table, closed by its X or the HUD button. */
export function HintPanel({
  bMax,
  terms,
  a,
  open,
  onClose,
}: TableProps & { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <aside className="hint-box enter w-full max-w-full" data-open="true">
      <div className="mb-1.5 flex items-center justify-between gap-4">
        <span className="label">{`${t('home.difficulty')} ${bMax}`}</span>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="grid size-6 place-items-center rounded-lg border border-line-strong text-fg-muted hover:border-accent hover:text-accent"
        >
          <X className="size-3.5" strokeWidth={2.4} />
        </button>
      </div>
      <WideTable bMax={bMax} terms={terms} a={a} />
    </aside>
  );
}
